import React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/styles';
import { useDispatch, useSelector } from 'react-redux';
import {
  Card,
  CardHeader,
  CardContent,
  Divider,
  Typography,
  Box,
  Paper,
  ButtonBase,
  Grid
} from '@material-ui/core';
import {
  themeModeSelector,
  setTheme
} from '../../../../redux/features/theme/themeSlice';
import Brightness4Icon from '@material-ui/icons/Brightness4';
import Brightness7Icon from '@material-ui/icons/Brightness7';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';

const useStyles = makeStyles((theme) => ({
  root: {},
  themeOption: {
    position: 'relative',
    width: '100%',
    borderRadius: theme.spacing(2),
    overflow: 'hidden',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    border: `3px solid transparent`,
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: theme.shadows[8]
    }
  },
  themeOptionSelected: {
    border: `3px solid ${theme.palette.primary.main}`,
    boxShadow: theme.shadows[4]
  },
  themePreview: {
    padding: theme.spacing(3),
    height: 180,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    position: 'relative'
  },
  previewHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(1.5),
    borderRadius: theme.spacing(1),
    backgroundColor: 'rgba(0, 0, 0, 0.05)'
  },
  previewContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    flex: 1
  },
  previewLine: {
    height: 8,
    borderRadius: 4,
    backgroundColor: 'rgba(0, 0, 0, 0.1)'
  },
  previewLineShort: {
    width: '60%'
  },
  themeLabel: {
    padding: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: theme.palette.background.default
  },
  themeName: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    fontWeight: 600
  },
  checkIcon: {
    color: theme.palette.primary.main,
    fontSize: 28
  },
  description: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(3),
    color: theme.palette.text.secondary
  },
  // Light theme preview colors
  lightPreview: {
    backgroundColor: '#FFFFFF',
    '& $previewHeader': {
      backgroundColor: '#F5F7FA',
      color: '#1A202C'
    },
    '& $previewLine': {
      backgroundColor: '#E2E8F0'
    }
  },
  // Dark theme preview colors
  darkPreview: {
    backgroundColor: '#1A202C',
    '& $previewHeader': {
      backgroundColor: '#2D3748',
      color: '#FFFFFF'
    },
    '& $previewLine': {
      backgroundColor: '#4A5568'
    }
  },
  benefitsList: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`
  },
  benefitItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
    '&:last-child': {
      marginBottom: 0
    }
  },
  benefitIcon: {
    marginTop: 2,
    fontSize: 20,
    color: theme.palette.primary.main
  }
}));

const ThemeSettings = (props) => {
  const { className, ...rest } = props;
  const classes = useStyles();
  const dispatch = useDispatch();
  const currentTheme = useSelector(themeModeSelector);

  const handleThemeChange = (mode) => {
    dispatch(setTheme({ mode }));
  };

  const themes = [
    {
      mode: 'light',
      name: 'Light Mode',
      description: 'Classic bright interface, perfect for daytime use',
      icon: <Brightness7Icon />,
      previewClass: classes.lightPreview
    },
    {
      mode: 'dark',
      name: 'Dark Mode',
      description: 'Easy on the eyes, ideal for low-light environments',
      icon: <Brightness4Icon />,
      previewClass: classes.darkPreview
    }
  ];

  const benefits = [
    { icon: '🎨', text: 'Personalize your experience with beautiful themes' },
    { icon: '👁️', text: 'Reduce eye strain with dark mode in low light' },
    { icon: '⚡', text: 'Instant switching - your preference is saved automatically' },
    { icon: '📱', text: 'Consistent across all your devices' }
  ];

  return (
    <Card {...rest} className={clsx(classes.root, className)}>
      <CardHeader
        title="Theme & Appearance"
        subheader="Choose how Remote Job looks to you"
      />
      <Divider />
      <CardContent>
        <Typography className={classes.description} variant="body2">
          Select your preferred theme. Your choice will be saved and applied across all pages.
        </Typography>

        <Grid container spacing={3}>
          {themes.map((theme) => {
            const isSelected = currentTheme === theme.mode;
            
            return (
              <Grid item xs={12} sm={6} key={theme.mode}>
                <ButtonBase
                  onClick={() => handleThemeChange(theme.mode)}
                  className={clsx(classes.themeOption, {
                    [classes.themeOptionSelected]: isSelected
                  })}
                >
                  <Paper elevation={0}>
                    <Box className={clsx(classes.themePreview, theme.previewClass)}>
                      <Box className={classes.previewHeader}>
                        <Box style={{ display: 'flex', gap: 6 }}>
                          <Box
                            style={{
                              width: 10,
                              height: 10,
                              borderRadius: '50%',
                              backgroundColor: '#FF5F57'
                            }}
                          />
                          <Box
                            style={{
                              width: 10,
                              height: 10,
                              borderRadius: '50%',
                              backgroundColor: '#FFBD2E'
                            }}
                          />
                          <Box
                            style={{
                              width: 10,
                              height: 10,
                              borderRadius: '50%',
                              backgroundColor: '#28CA42'
                            }}
                          />
                        </Box>
                        {theme.icon}
                      </Box>
                      <Box className={classes.previewContent}>
                        <Box className={classes.previewLine} />
                        <Box className={clsx(classes.previewLine, classes.previewLineShort)} />
                        <Box className={classes.previewLine} />
                        <Box className={clsx(classes.previewLine, classes.previewLineShort)} />
                      </Box>
                    </Box>
                    <Box className={classes.themeLabel}>
                      <Box className={classes.themeName}>
                        {theme.icon}
                        <Box>
                          <Typography variant="subtitle1">
                            {theme.name}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {theme.description}
                          </Typography>
                        </Box>
                      </Box>
                      {isSelected && (
                        <CheckCircleIcon className={classes.checkIcon} />
                      )}
                    </Box>
                  </Paper>
                </ButtonBase>
              </Grid>
            );
          })}
        </Grid>

        <Box className={classes.benefitsList}>
          <Typography variant="subtitle2" gutterBottom>
            <strong>Why customize your theme?</strong>
          </Typography>
          {benefits.map((benefit, index) => (
            <Box key={index} className={classes.benefitItem}>
              <span className={classes.benefitIcon} role="img" aria-label="benefit">
                {benefit.icon}
              </span>
              <Typography variant="body2" color="textSecondary">
                {benefit.text}
              </Typography>
            </Box>
          ))}
        </Box>
      </CardContent>
    </Card>
  );
};

ThemeSettings.propTypes = {
  className: PropTypes.string
};

export default ThemeSettings;

